import {
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@heroicons/react/20/solid';

interface PaginationProps {
  pageIndex: number;
  pageSize: number;
  totalCount: number;
  onPageChange: (pageIndex: number) => void;
}

export const Pagination = ({ pageIndex, pageSize, totalCount, onPageChange }: PaginationProps) => {
  const currentPage = pageIndex + 1;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const startItem = totalCount === 0 ? 0 : pageIndex * pageSize + 1;
  const endItem = Math.min(totalCount, (pageIndex + 1) * pageSize);

  const canPreviousPage = pageIndex > 0;
  const canNextPage = currentPage < totalPages;

  return (
    <div className="flex items-center justify-between my-3">
      <div className="flex flex-1 flex-wrap items-center gap-x-5 gap-y-2">
        <div>
          <nav aria-label="ページネーション" className="isolate inline-flex -space-x-px rounded">
            <button
              type="button"
              onClick={() => onPageChange(0)}
              disabled={!canPreviousPage}
              className="relative inline-flex items-center rounded-l px-2 py-2 text-neutral-400 ring-1 ring-inset ring-neutral-200 hover:bg-neutral-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span className="sr-only">最初のページへ</span>
              <ChevronDoubleLeftIcon aria-hidden="true" className="size-5" />
            </button>
            <button
              type="button"
              onClick={() => onPageChange(pageIndex - 1)}
              disabled={!canPreviousPage}
              className="relative inline-flex items-center px-2 py-2 text-neutral-400 ring-1 ring-inset ring-neutral-200 hover:bg-neutral-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span className="sr-only">前へ</span>
              <ChevronLeftIcon aria-hidden="true" className="size-5" />
            </button>
            <div className="relative inline-flex items-center px-4 py-2 text-sm font-semibold border border-neutral-200 focus:outline-offset-0">
              <span className="mr-1">ページ</span>
              <strong>
                {currentPage} / {totalPages}
              </strong>
            </div>
            <button
              type="button"
              onClick={() => onPageChange(pageIndex + 1)}
              disabled={!canNextPage}
              className="relative inline-flex items-center px-2 py-2 text-neutral-400 ring-1 ring-inset ring-neutral-200 hover:bg-neutral-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span className="sr-only">次へ</span>
              <ChevronRightIcon aria-hidden="true" className="size-5" />
            </button>
            <button
              type="button"
              onClick={() => onPageChange(totalPages - 1)}
              disabled={!canNextPage}
              className="relative inline-flex items-center rounded-r px-2 py-2 text-neutral-400 ring-1 ring-inset ring-neutral-200 hover:bg-neutral-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span className="sr-only">最後のページへ</span>
              <ChevronDoubleRightIcon aria-hidden="true" className="size-5" />
            </button>
          </nav>
        </div>
        <div className="flex items-center">
          <p className="text-sm">
            <span className="font-medium">{startItem}</span> から{' '}
            <span className="font-medium">{endItem}</span> を表示（全{' '}
            <span className="font-medium">{totalCount}</span> 件）
          </p>
        </div>
      </div>
    </div>
  );
};
